import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { FirebaseError } from 'firebase/app';
import {
  GoogleAuthProvider,
  linkWithCredential,
  onAuthStateChanged,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type AuthCredential,
  type User,
} from 'firebase/auth';

import { auth } from './firebase.js';
import { dashboardPushClient } from './push/client.js';
import { googleErrorKey } from './login-errors.js';
import { logger } from './logger.js';
import { watchOwnClaimsWatermark } from './repositories/user-claims-repo.js';

/**
 * Outcome of a "Continue with Google" attempt. `link-with-password` is the
 * account-model resolution: the operator already has an email+password account
 * for this Google email (created by `tenantBootstrap`), so Google can't create
 * a second one — the caller collects the password once and links the Google
 * credential onto the existing account (`linkGoogleAfterPassword`).
 */
export type GoogleSignInOutcome =
  | { readonly status: 'success' }
  | {
      readonly status: 'link-with-password';
      readonly email: string;
      readonly pendingCredential: AuthCredential;
    }
  | { readonly status: 'error'; readonly errorKey: string | null };

/**
 * AuthProvider — singleton for the dashboard.
 *
 * - Subscribes to onAuthStateChanged so consumers re-render on sign-in / sign-out.
 * - Consumes a `#signInToken=...` URL fragment on first mount (the marketing → dashboard
 *   cross-origin handoff per ADR-0015), signs in with the custom token, and strips
 *   the fragment from the URL so refreshes don't replay it.
 * - Exposes email+password sign-in + password-reset for the RETURNING-user login
 *   screen (the token handoff only covers the one-time post-signup landing; once
 *   that session ends the operator/super-admin gets back in via `signInWithEmail`).
 *   On success `onAuthStateChanged`/`onIdTokenChanged` below take over exactly as
 *   they do for the token path — no separate routing branch.
 * - Exposes refreshed claims via onIdTokenChanged so role/tenant context picks up
 *   changes from claimsSync without a full page reload.
 */

export interface AuthState {
  readonly user: User | null;
  readonly claims: Record<string, unknown> | null;
  readonly loading: boolean;
  readonly signOut: () => Promise<void>;
  /**
   * Force a token refresh so a server-side claims change lands immediately.
   *
   * This is a UX accelerator, NOT the security boundary — `firestore.rules`
   * refuses a stale token whether or not any browser cooperates (G2/ADR-0007).
   * What this buys is that a promoted or demoted user sees their new access in
   * about a second instead of a wall of permission-denied errors until Firebase
   * happens to refresh on its own.
   */
  readonly forceRefreshClaims: () => Promise<void>;
  /**
   * Email+password sign-in for returning operators AND super-admins (identical
   * path — the claim on the resulting token gates what they see afterward).
   * Resolves on success; rejects with the raw `FirebaseError` so the caller can
   * map `error.code` → human copy via `loginErrorKey`.
   */
  readonly signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Send a password-reset email. Rejects with the raw `FirebaseError`. */
  readonly sendPasswordReset: (email: string) => Promise<void>;
  /**
   * "Continue with Google" (primary, one-tap sign-in for operators AND
   * super-admins). Never rejects — returns a discriminated {@link GoogleSignInOutcome}
   * so the caller can drive the popup-cancel / config-error / account-link UI
   * without touching `firebase/auth` itself.
   */
  readonly signInWithGoogle: () => Promise<GoogleSignInOutcome>;
  /**
   * Finish the account link: sign in with the existing password, then attach
   * the pending Google credential so future Google taps just work. Rejects with
   * the raw `FirebaseError` (wrong password → caller maps via `loginErrorKey`).
   */
  readonly linkGoogleAfterPassword: (
    email: string,
    password: string,
    pendingCredential: AuthCredential,
  ) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  // Consume the cross-origin custom-token handoff exactly once.
  useEffect(() => {
    const fragment = window.location.hash;
    if (!fragment.includes('signInToken=')) return;

    const params = new URLSearchParams(fragment.slice(1));
    const token = params.get('signInToken');
    if (!token) return;

    // Strip the fragment immediately so refreshes / browser-back don't replay it.
    const { pathname, search } = window.location;
    window.history.replaceState({}, '', pathname + search);

    signInWithCustomToken(auth, token).catch((err: unknown) => {
      logger.error('signInWithCustomToken failed', { error: String(err) });
    });
  }, []);

  // Mirror of `claims` for the watermark listener, so it can compare against
  // the live token without re-subscribing every time claims change.
  const claimsRef = useRef<Record<string, unknown> | null>(null);
  useEffect(() => {
    claimsRef.current = claims;
  }, [claims]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    const unsubToken = onIdTokenChanged(auth, async (u) => {
      if (!u) {
        setClaims(null);
        return;
      }
      try {
        const result = await u.getIdTokenResult();
        setClaims(result.claims);
      } catch (e) {
        logger.error('getIdTokenResult failed', { error: String(e) });
      }
    });
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, []);

  // G2 — watch our own revocation watermark. When the server re-mints this
  // user's claims it advances `revokedAt`; refreshing the token republishes
  // `claims` through `onIdTokenChanged` above, so nothing else needs to know.
  // Debounced: several rapid staff edits must not become several token fetches.
  useEffect(() => {
    if (!user) return;
    let lastRefresh = 0;
    const unsub = watchOwnClaimsWatermark(user.uid, (revokedAt) => {
      if (revokedAt === null) return;
      const stamped = claimsRef.current?.['claimsRefreshedAt'];
      const current = typeof stamped === 'number' ? stamped : 0;
      if (current >= revokedAt) return; // token already current
      const now = Date.now();
      if (now - lastRefresh < 5000) return;
      lastRefresh = now;
      user.getIdToken(true).catch((e: unknown) => {
        logger.error('forced claims refresh failed', { error: String(e) });
      });
    });
    return unsub;
  }, [user]);

  const value: AuthState = {
    user,
    claims,
    loading,
    forceRefreshClaims: async () => {
      if (!user) return;
      await user.getIdToken(true);
    },
    // Push hygiene first, while the outgoing user can still delete their own
    // fcmTokens row — otherwise this device keeps getting their bookings and
    // payments after sign-out. cleanupForSignOut never throws.
    signOut: async () => {
      const outgoingUid = auth.currentUser?.uid;
      if (outgoingUid) await dashboardPushClient.cleanupForSignOut(outgoingUid);
      await fbSignOut(auth);
    },
    signInWithEmail: (email, password) =>
      signInWithEmailAndPassword(auth, email, password).then(() => undefined),
    sendPasswordReset: (email) => sendPasswordResetEmail(auth, email),
    signInWithGoogle: async () => {
      try {
        await signInWithPopup(auth, new GoogleAuthProvider());
        return { status: 'success' };
      } catch (err) {
        if (err instanceof FirebaseError) {
          // The email already belongs to a password (operator) account →
          // don't fail; hand the caller the pending Google credential + email
          // so it can collect the password once and link (not fork a 2nd acct).
          if (err.code === 'auth/account-exists-with-different-credential') {
            const pendingCredential = GoogleAuthProvider.credentialFromError(err);
            const emailRaw = err.customData?.['email'];
            const email = typeof emailRaw === 'string' ? emailRaw : null;
            if (pendingCredential && email) {
              return { status: 'link-with-password', email, pendingCredential };
            }
          }
          logger.warn('google sign-in failed', { code: err.code });
        }
        return { status: 'error', errorKey: googleErrorKey(err) };
      }
    },
    // Sign in FIRST (proves the password), THEN attach Google. Both awaits run
    // fully in order; the sign-in flips auth state mid-flow, so the caller
    // (LinkPanel) guards its post-await setState against unmount.
    linkGoogleAfterPassword: async (email, password, pendingCredential) => {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await linkWithCredential(cred.user, pendingCredential);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
